import React, {useEffect, useState} from 'react'
import {TObservablePage} from '../../types/commerce'
import {flowResult} from 'mobx'
import {observer} from 'mobx-react-lite'
import {useCategoryStore, useContentStore, useGlobalStore, useOrderStore} from '../../store/hooks/useStore'
import {Layout} from '../../components/layout'
import {Container} from '../../vanilla'
import PageNotFound from '../../pages/page-not-found'
import CategoryPage from '../category'
import {SlotContentType} from '../category'
import {DisplaySlots} from '../category'

//@ts-ignore
import {Seo} from '../../components/seo'
import InstantSearch from '../../components/algolia/instantsearch'
import {Configure} from 'react-instantsearch-hooks-web'
import {Slot} from '../../components/cms/Slot'
import {
  PLPSkeletonBreadcrumbs,
  PLPSkeletonGrid,
  PLPSkeletonLayout,
  PLPSkeletonStatsFilters,
  PLPSkeletonTiles,
} from '../../components/skeletons'
import {EditOrderStickyBanner} from '../../components/edit-order-sticky-banner'
import { HITS_PER_PAGE } from '../../constants'
import { useLocation } from 'react-router-dom'

interface ContentProps {
  deliveryId?: string
  pageType?: 'CATEGORY' | 'CONTENT'
  categoryId?: string
  serverState: any
  serverUrl: string
}

const CATEGORY_BANNER_SCHEMA = 'https://www.iceland.co.uk/slot/category-banner-slot.json'
const CATEGORY_HEADLINE_BANNER_SCHEMA =
  'https://www.iceland.co.uk/content/category-headline-banner.json'
const IN_GRID_PROMO_SCHEMA = 'https://www.iceland.co.uk/slot/in-grid-positioning-slot.json'

const Content: TObservablePage<ContentProps> = observer(
  ({deliveryId, pageType, categoryId, serverUrl}) => {
    const contentStore = useContentStore()
    const {fetchAllCategories, status} = useCategoryStore()
    const {editMode} = useOrderStore()
    const {
      selectedAvailabilityDayOffset,
      selectedStoreId,
      dataLoadedForAccuratePricingAndAvailabilty,
    } = useGlobalStore()
    const [showPageNotFound, setShowPageNotFound] = useState(false)
    const location = useLocation()

    const path = location.pathname
    const route = contentStore.routeMap[path]
    const routeInfo = route?.split('|');
    deliveryId = deliveryId || (routeInfo?.length > 0 ? routeInfo[0] : undefined);

    const availabilityFilter = `matrixAvailability.${selectedAvailabilityDayOffset}:-${selectedStoreId}`
    const inStockFilter = `in_stock:true`
    
    const getCategories = async () => await fetchAllCategories()
    useEffect(() => {
      if (status === 'idle') {
        getCategories()
      }  
    })

    useEffect(() => {
      if (!deliveryId) {
        setShowPageNotFound(true)
      } else {
        setShowPageNotFound(false)
      }
    }, [dataLoadedForAccuratePricingAndAvailabilty, deliveryId])

    if (showPageNotFound) {
      return (
        <Layout>
          <Seo title="404" />
          <PageNotFound />
        </Layout>
      )
    }

    const page = contentStore.contentById[deliveryId]
    let hasCategoryHeadlineBanner = false
    const bannerSlot =
      page?.slots &&
      page?.slots?.length > 0 &&
      page?.slots?.map((slotGroup: SlotContentType, idx: number) => {
        if (slotGroup?._meta?.schema === CATEGORY_BANNER_SCHEMA) {
          if (
            slotGroup.default?.find(({_meta}) => _meta.schema === CATEGORY_HEADLINE_BANNER_SCHEMA)
          ) {
            hasCategoryHeadlineBanner = true
          }
          return <DisplaySlots slots={slotGroup} key={`${slotGroup?._meta?.deliveryId}-${idx}`} />
        }
      })

    const inGridPromos: SlotContentType =
      page?.slots &&
      page?.slots?.length > 0 &&
      page?.slots?.find(
        (slotGroup: SlotContentType) => slotGroup?._meta?.schema === IN_GRID_PROMO_SCHEMA,
      )

    if (!page && pageType === 'CATEGORY' && !dataLoadedForAccuratePricingAndAvailabilty) {
      return (
        <PLPSkeletonLayout>
          <PLPSkeletonBreadcrumbs />
          <PLPSkeletonStatsFilters />
          <PLPSkeletonGrid>
            <PLPSkeletonTiles />
          </PLPSkeletonGrid>
        </PLPSkeletonLayout>
      )
    }

    // Create the seo object
    const seo = {
      pageTitle: page?.seo?.pageTitle || page?.SEO?.pageTitle || `${page?.name} | Iceland Foods`,
      pageDescription: page?.seo?.pageDescription || page?.SEO?.pageDescription,
    }

    return (
      <Layout>
        {pageType === 'CATEGORY' && editMode && <EditOrderStickyBanner />}
        {page && <Seo title={seo.pageTitle} description={seo.pageDescription} />}
        <Container
          padding="0px"
          style={page && pageType === 'CATEGORY' ? {minHeight: '800px'} : {}}
        >
          {page && pageType === 'CATEGORY' && (
            <CategoryPage
              slots={page?.slots}
              seo={seo}
              categoryId={categoryId}
              name={page?.name}
              categoryTree={page?.parentCategoryTree}
            >
              {dataLoadedForAccuratePricingAndAvailabilty ? <InstantSearch
                categoryTree={page?.parentCategoryTree}
                seo={seo}
                serverState={{}}
                serverUrl={serverUrl}
                category={{name: page?.name, id: categoryId || ''}}
                contentAboveHeader={bannerSlot}
                hasCategoryHeadlineBanner={hasCategoryHeadlineBanner}
                inGridPromos={inGridPromos}
                presentationAttributes={page.presentationAttributes}
                h2={{
                  textAlignment: page?.pageHeading?.textAlignment?.toLowerCase(),
                  heading2: page?.pageHeading?.heading2,
                }}
                pageName={page?.pageHeading?.heading}
              >
                <Configure
                  clickAnalytics
                  maxValuesPerFacet={100}
                  facetFilters={['priceRange', 'brand', 'categoryPageId', availabilityFilter, inStockFilter]}
                  facets={['priceRange', 'brand', 'categoryPageId','filterPackSize','filterBrand','filterType','filterWeight','filterLitre','manufacturerSKU','filterServing', 'buyingSubGroup1','buyingSubGroup2','chamber','buyingGroup','primary_category_id']}
                  filters={`categoryPageId:${categoryId}`}
                  hitsPerPage={HITS_PER_PAGE}
                  highlightPostTag='__/ais-highlight__'
                  highlightPreTag='__ais-highlight__'
                />
              </InstantSearch>: 
              <PLPSkeletonLayout>
              <PLPSkeletonBreadcrumbs />
              <PLPSkeletonStatsFilters />
              <PLPSkeletonGrid>
                <PLPSkeletonTiles />
              </PLPSkeletonGrid>
            </PLPSkeletonLayout>}
            </CategoryPage>
          )}
        </Container>
        {page && pageType !== 'CATEGORY' && <Slot data={page} />}
      </Layout>
    )
  },
)

Content.getProps = async ({store, location, res, req}) => {
  await flowResult(store.contentStore.buildRoutingMapAndNavMenu())

  const path = location.pathname
  const route = store.contentStore?.routeMap[path]

  if (route) {
    const [deliveryId, pageType, categoryId] = route.split('|')
    if (typeof window === 'undefined') {
      await flowResult(store.contentStore.fetchItemById(deliveryId))

      const protocol = req.headers.referer?.split('://')[0] || 'https'
      const serverUrl = `${protocol}://${req.headers.host}${req.originalUrl}`

      const serverState = {}

      return {serverState, serverUrl, deliveryId, pageType, categoryId}
    } else {
      store.contentStore.fetchItemById(deliveryId)
    }

    return {deliveryId, pageType, categoryId}
  } else {
    if (res) {
      res.status(404)
    }
  }
}

export default Content
